import React, { useEffect, useRef } from 'react'
import { Box, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material'
import { IconButtonWithTooltip, PhoneNumberField } from 'components'
import DeleteIcon from '@mui/icons-material/Delete'
import { type UsPhone } from 'services'
import { PHONE_TYPES } from 'utils'

interface AddressPhoneCardProps<T extends UsPhone> {
  phone: T
  disabled: boolean
  onDeletePhone: (phone: T) => void
  onUpdatePhone: (phone: T) => void
  address?: { phones: T[] }
}

const phoneTypeOptions = [
  {
    id: PHONE_TYPES.PHONE,
    label: 'Phone'
  },
  {
    id: PHONE_TYPES.FAX,
    label: 'Fax'
  },
  {
    id: PHONE_TYPES.TTY_TDD,
    label: 'TTY/TDD'
  },
  {
    id: PHONE_TYPES.GENERAL_INFORMATION,
    label: 'General Information'
  },
  {
    id: PHONE_TYPES.RECORDER_INFORMATION,
    label: 'Recorder Information'
  }
]

export const PhoneCard = <T extends UsPhone>({
  phone,
  disabled,
  onDeletePhone,
  onUpdatePhone,
  address
}: AddressPhoneCardProps<T>): React.ReactElement | null => {
  const typeSelectRef = useRef<HTMLInputElement>(null)

  const calculateNextDisplayOrder = (typeId: number): number => {
    if (address?.phones === null) return 1

    const phonesOfSameType = address?.phones.filter(p => p.id !== phone.id && p.typeId === typeId) ?? []
    let nextDisplayOrder = 1

    if (phonesOfSameType.length > 0) {
      nextDisplayOrder = Math.max(
        ...phonesOfSameType.map(p => p.displayOrder ?? 0)
      ) + 1
    } else {
      nextDisplayOrder = 1
    }

    return nextDisplayOrder
  }

  useEffect(() => {
    if (phone.id !== null && phone.id < 0 && !disabled && typeSelectRef.current != null) {
      typeSelectRef.current.focus()
    }
  }, [phone.id, disabled])

  useEffect(() => {
    if (phone.typeId !== 0 && !isNaN(phone.typeId) && (phone.displayOrder == null || phone.displayOrder === 0)) {
      onUpdatePhone({
        ...phone,
        displayOrder: calculateNextDisplayOrder(phone.typeId)
      } satisfies T)
    }
  }, [phone.id, phone.typeId, phone.displayOrder, address, onUpdatePhone])

  if (phone.id == null) {
    return null
  }

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <TextField
        id={`area-code-${phone.id}`}
        name={`area-code-${phone.id}`}
        label="Area Code"
        value={phone.areaCode ?? ''}
        disabled={disabled}
        onChange={(e) => {
          onUpdatePhone({
            ...phone,
            areaCode: e.target.value
          } satisfies T)
        }}
        inputProps={{
          maxLength: 3,
          autoComplete: 'tel-area-code',
          'aria-label': 'Area Code'
        }}
        sx={{ width: '15%' }}
      />
        <PhoneNumberField
            id={`phone-number-${phone.id}`}
            name={`phone-number-${phone.id}`}
            sx={{ width: '30%' }}

            label="Phone Number"
            phoneNumber={phone.phoneNumber ?? ''}
            handlePhoneChange={(value) => {
              onUpdatePhone({
                ...phone,
                phoneNumber: value
              } satisfies T)
            } }
            disabled={disabled}
        />
      <TextField
        id={`extension-${phone.id}`}
        name={`extension-${phone.id}`}
        label="Extension"
        value={phone.extension ?? ''}
        disabled={disabled}
        onChange={(e) => {
          onUpdatePhone({
            ...phone,
            extension: e.target.value
          } satisfies T)
        }}
        inputProps={{
          maxLength: 10,
          autoComplete: 'tel-extension',
          'aria-label': 'Extension'
        }}
        sx={{ width: '15%' }}
      />
      <FormControl variant="outlined" size="small" sx={{
        width: '25%',
        height: '56px'
      }}>
        <InputLabel id={`phone-type-label-${phone.id}`} htmlFor={`phone-type-${phone.id}`}>
          Type
        </InputLabel>
        <Select
          labelId={`phone-type-label-${phone.id}`}
          id={`phone-type-${phone.id}`}
          name={`phone-type-${phone.id}`}
          value={phone.typeId}
          label="Type"
          disabled={disabled}
          onChange={(e) => {
            const newTypeId = Number(e.target.value)
            let updatedPhone: T = {
              ...phone,
              typeId: newTypeId
            }

            if (newTypeId !== phone.typeId) {
              updatedPhone = {
                ...updatedPhone,
                displayOrder: calculateNextDisplayOrder(newTypeId)
              }
            }

            onUpdatePhone(updatedPhone)
          }}
          inputProps={{
            'aria-label': 'Phone Type',
            ref: typeSelectRef // Add the ref here
          }}
          sx={{ height: '100%' }}
        >
          {phoneTypeOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        width: '10%'
      }}>
        <Typography variant="body1" color="text.secondary">
          {phone.displayOrder != null ? `#${phone.displayOrder}` : '-'}
        </Typography>
      </Box>
      <Box sx={{
        display: 'flex',
        gap: 1,
        width: '15%',
        justifyContent: 'flex-end'
      }}>
        <IconButtonWithTooltip
          label="Delete Phone"
          color="secondary"
          onClick={() => {
            onDeletePhone(phone)
          }}
          icon={<DeleteIcon/>}
          disabled={disabled}
        />
      </Box>
    </Stack>
  )
}
